import {
  DIRECTION_DOWN,
  DIRECTION_RIGHT,
  DIRECTION_LEFT,
  PLACEMENT_TYPE_CIABATTA_BLAST,
  PLACEMENT_TYPE_ROAMING_ENEMY,
  PLACEMENT_TYPE_BULLET_PICKUP,
  DIRECTION_UP,
  PLACEMENT_TYPE_SWITCH,
  PLACEMENT_TYPE_BULLET,
  PLACEMENT_TYPE_HERO,
  PLACEMENT_TYPE_WARNING,
} from "../helpers/consts";
import { BodyPlacement } from "./BodyPlacement";
import { TILES } from "../helpers/tiles";
import { shuffle } from "../helpers/shuffle";
import CharacterBody from "../components/object-graphics/CharacterBody";
import { randomFromArray } from "../helpers/randomFromArray";
import sounds, { SFX } from "../classes/Sounds";

const BEHAVIORS = {
  TACKLE: "TACKLE",
  BLAST: "BLAST",
  SPAWN_ROAMINGS: "SPAWN_ROAMINGS",
  HORIZONTAL_WALK: "HORIZONTAL_WALK",
  SPAWN_BULLET_PICKUP: "SPAWN_BULLET_PICKUP",
};

export class CiabattaPlacement extends BodyPlacement {
  constructor(properties, level) {
    super(properties, level);
    this.framesUntilAiMove = this.waitFramesBeforeAIMove;
    this.normalFrameCoord = TILES.CIABATTA1;
    this.introFrames = 60;

    this.healthPoints = 3; // 3;
    this.painFramesRemaining = 0;
    this.deathDisappearFramesRemaining = 0;

    //This is general behavior progress
    this.behaviorMode = BEHAVIORS.HORIZONTAL_WALK;
    this.behaviorFrameCount = 0;

    //These are related to MOVING within the tackle behavior
    this.framesUntilAiMove = 5;
    this.waitFramesBeforeAIMove = 11;
    this.tackleMoveOrder = [
      DIRECTION_DOWN,
      DIRECTION_LEFT,
      DIRECTION_RIGHT,
      DIRECTION_RIGHT,
      DIRECTION_LEFT,
      DIRECTION_UP,
    ];
    this.tackleMoveIndex = 0;
    this.tackleWarningPosition = [];
    this.tackleWarningInstance = null;

    this.movesLeft = 3;
    this.priorityDirection = DIRECTION_LEFT;

    //Movement speed when walking
    this.travelPixelsPerFrame = 2;

    // This is just visual
    this.animationFramesLeft = 30;

    // Optional ability to spawn bullets
    this.spawnsBullets = properties.spawnsBullets ?? false;
  }

  get isDead() {
    return this.healthPoints <= 0;
  }

  get tileFrame() {
    if (this.painFramesRemaining > 0) {
      return TILES.CIABATTA_PAIN;
    }
    if (this.isDead) {
      return TILES.CIABATTA_DEAD;
    }

    return this.normalFrameCoord;
  }

  tick() {
    this.tickMovingPixelProgress();
    this._downtickAnimationFrames();
    this._downtickPainFrames();
    this._downtickDeathFrames();

    //Intro - do not allow behavior until done
    if (this.introFrames > 0) {
      this.introFrames -= 1;
      return;
    }

    // Down tick
    if (this.behaviorMode === BEHAVIORS.SPAWN_ROAMINGS) {
      this._workOnSpawnRoamingBehavior();
    }
    if (this.behaviorMode === BEHAVIORS.BLAST) {
      this._workOnBlastBehavior();
    }
    if (this.behaviorMode === BEHAVIORS.TACKLE) {
      this._workOnTackleBehavior();
    }
    if (this.behaviorMode === BEHAVIORS.HORIZONTAL_WALK) {
      this._workOnHorizontalWalk();
    }
    if (this.behaviorMode === BEHAVIORS.SPAWN_BULLET_PICKUP) {
      this._workOnSpawnBulletBehavior();
    }

    //Uptick progress
    this.behaviorFrameCount += 1;
  }

  _changeBehavior() {
    // Stop if died during previous behavior
    if (this.isDead) {
      return;
    }

    //Pick "business as usual behavior"
    if (this.movesLeft > 0) {
      this.movesLeft -= 1;
      this.behaviorMode = BEHAVIORS.HORIZONTAL_WALK;
    } else {
      this.movesLeft = randomFromArray([3, 3, 3, 2, 1, 1]); //For next time, be sure to walk

      //Pick a random one
      const possibleNext = [
        BEHAVIORS.SPAWN_BULLET_PICKUP,
        BEHAVIORS.BLAST,
        BEHAVIORS.SPAWN_ROAMINGS,
        BEHAVIORS.TACKLE,
      ];
      //Be more likely to Tackle if health is low
      if (this.healthPoints === 1) {
        possibleNext.push(BEHAVIORS.TACKLE);
      }

      const filtered = possibleNext.filter((candidate) => {
        return this.behaviorMode !== candidate;
      });
      this.behaviorMode = randomFromArray(filtered);
    }

    //Override If Hero is on same Y (on top), just use WALK
    const hero = this.level.heroRef;
    if (hero.y === 1) {
      this.behaviorMode = BEHAVIORS.HORIZONTAL_WALK;
    }

    //Start fresh new behavior
    this.behaviorFrameCount = 0;
  }

  _workOnSpawnBulletBehavior() {
    //Drop a new bullet for Hero to grab
    if (this.behaviorFrameCount === 1) {
      this._spawnBulletPickup();
    }
    // Wait a bit before moving on
    if (this.behaviorFrameCount === 120) {
      this._changeBehavior();
    }
  }

  _spawnBulletPickup() {
    //Only do this if feature is turned on for this placement
    if (!this.spawnsBullets) {
      return;
    }

    //Cancel if there are already 2 bullets around
    const existingBullets = this.level.placements.filter((p) => {
      return p.type === PLACEMENT_TYPE_BULLET;
    });
    if (existingBullets.length >= 2) {
      return;
    }
    const newPickup = randomFromArray([
      { x: 3, y: 5 },
      { x: 5, y: 3 },
      { x: 6, y: 7 },
      { x: 6, y: 6 },
      { x: 4, y: 4 },
    ]);
    this.level.spawnLivePlacement({
      x: newPickup.x,
      y: newPickup.y,
      type: PLACEMENT_TYPE_BULLET_PICKUP,
    });
  }

  _getNextWalkDirection() {
    const hero = this.level.heroRef;
    if (hero.y === this.y) {
      return hero.x < this.x ? DIRECTION_LEFT : DIRECTION_RIGHT;
    }
    // Reverse if near walls
    if (this.x === 1) {
      return DIRECTION_RIGHT;
    }
    if (this.x === 7) {
      return DIRECTION_LEFT;
    }
    return this.priorityDirection;
  }

  _workOnHorizontalWalk() {
    // Walk when behavior starts
    if (this.behaviorFrameCount === 1) {
      this.priorityDirection = this._getNextWalkDirection();
      this._move(this.priorityDirection);
    }

    // End the walking
    if (this.behaviorFrameCount === 45) {
      this._changeBehavior();
    }
  }

  _workOnSpawnRoamingBehavior() {
    // Create the clowns
    if (this.behaviorFrameCount === 1) {
      this._createRoamingWarnings();
    }
    if (this.behaviorFrameCount === 60) {
      this._createRoamingEnemies();
    }
    // Drop a bullet pickup
    if (this.behaviorFrameCount === 120) {
      this._spawnBulletPickup();
    }
    // Remove them before the move ends
    if (this.behaviorFrameCount === 440) {
      this._removeRoamingEnemies();
    }
    // Move is over
    if (this.behaviorFrameCount === 460) {
      this._changeBehavior();
    }
  }

  _createRoamingWarnings() {
    const hero = this.level.heroRef;

    const positions = shuffle([
      { x: hero.x, y: hero.y - 2 },
      { x: hero.x, y: hero.y + 2 },
      { x: hero.x - 2, y: hero.y },
      { x: hero.x + 2, y: hero.y },
    ]).filter((newEnemy, i) => {
      if (i === 0) {
        return false;
      }
      if (newEnemy.x <= 0 || newEnemy.x > 9) {
        return false;
      }
      if (newEnemy.y > 8) {
        return false;
      }
      return true;
    });

    positions.forEach((p) => {
      this.level.spawnLivePlacement({
        type: PLACEMENT_TYPE_WARNING,
        x: p.x,
        y: p.y,
      });
    });
  }

  _createRoamingEnemies() {
    sounds.playSfx(SFX.ENEMIES_APPEAR);

    this.level.placements
      .filter((p) => p.type === PLACEMENT_TYPE_WARNING)
      .forEach((p) => {
        this.level.spawnLivePlacement({
          type: PLACEMENT_TYPE_ROAMING_ENEMY,
          x: p.x,
          y: p.y,
        });
        this.level.removeLivePlacement(p);
      });
  }

  _removeRoamingEnemies() {
    this.level.placements.forEach((p) => {
      if (p.type === PLACEMENT_TYPE_ROAMING_ENEMY) {
        this.level.removeLivePlacement(p);
      }
    });
  }

  _workOnBlastBehavior() {
    // Create the wave of bullets
    if (this.behaviorFrameCount === 1) {
      this._createBlast();
    }
    //End the behavior
    if (this.behaviorFrameCount === 100) {
      this._changeBehavior();
    }
  }

  _createBlast() {
    const patterns = [
      [DIRECTION_DOWN],
      [DIRECTION_LEFT, DIRECTION_DOWN],
      [DIRECTION_RIGHT, DIRECTION_DOWN],
      [DIRECTION_LEFT, DIRECTION_DOWN, DIRECTION_DOWN],
      [DIRECTION_RIGHT, DIRECTION_DOWN, DIRECTION_DOWN],
    ];
    patterns.forEach((p) => {
      this.level.spawnLivePlacement({
        type: PLACEMENT_TYPE_CIABATTA_BLAST,
        x: this.x,
        y: this.y,
        pattern: p,
      });
    });
  }

  _workOnTackleBehavior() {
    //Warn where he is going to go
    if (this.behaviorFrameCount === 1) {
      //Spawn warning, save a ref to it.
      this.tackleWarningPosition = [
        this.level.heroRef.x,
        this.level.heroRef.y - 1,
      ];
      this.tackleWarningInstance = this.level.spawnLivePlacement({
        type: PLACEMENT_TYPE_WARNING,
        x: this.tackleWarningPosition[0],
        y: this.tackleWarningPosition[1],
      });
    }

    //Jump there
    if (this.behaviorFrameCount === 40) {
      //Remove the warning space
      this.level.removeLivePlacement(this.tackleWarningInstance);

      sounds.playSfx(SFX.JUMP);
      this.tackleMoveIndex = 0; //Start fresh
      this.level.updateLivePlacement(this, {
        x: this.tackleWarningPosition[0],
        y: this.tackleWarningPosition[1],
      });
    }

    // Start tackle pattern
    if (this.behaviorFrameCount >= 50 && this.behaviorFrameCount <= 145) {
      //Keep a count of how many frames until we change spaces
      this.framesUntilAiMove -= 1;
      if (this.framesUntilAiMove === 0) {
        // Work on the pattern, don't do anything if we are done with the pattern
        if (this.tackleMoveOrder[this.tackleMoveIndex]) {
          this._move(this.tackleMoveOrder[this.tackleMoveIndex]);
        }
        this.tackleMoveIndex += 1;

        //Reset the move frame counter
        this.framesUntilAiMove = 8;
      }
    }

    //Warp back to the top
    if (this.behaviorFrameCount === 180) {
      // Nudge to the left if inline with purple block
      let newX = this.x;
      if (this.x >= 8) {
        newX = 7;
      }
      this.level.updateLivePlacement(this, {
        x: newX,
        y: 1,
      });
      //Check for bullets (because we warped)
      this._handleCollisions();
    }

    // End the tackle
    if (this.behaviorFrameCount === 210) {
      this._changeBehavior();
    }
  }

  _spawnDoorSwitch() {
    this.level.spawnLivePlacement({
      x: 5,
      y: 3,
      type: PLACEMENT_TYPE_SWITCH,
    });
  }

  _downtickDeathFrames() {
    if (this.deathDisappearFramesRemaining > 0) {
      this.deathDisappearFramesRemaining -= 1;

      if (this.deathDisappearFramesRemaining === 45) {
        // Give Switch for Hero to access Goal
        sounds.playSfx(SFX.SOMETHING_APPEARS);
        this._spawnDoorSwitch();
      }

      if (this.deathDisappearFramesRemaining === 0) {
        //Disappear
        sounds.playSfx(SFX.JUMP);
        this.level.removeLivePlacement(this);
      }
    }
  }

  _downtickPainFrames() {
    if (this.painFramesRemaining > 0) {
      this.painFramesRemaining -= 1;
      if (this.painFramesRemaining === 0) {
        //this.level.emit();
      }
    }
  }

  _downtickAnimationFrames() {
    if (this.animationFramesLeft > 0) {
      this.animationFramesLeft -= 1;
      if (this.animationFramesLeft === 0) {
        this.normalFrameCoord =
          this.normalFrameCoord === TILES.CIABATTA1
            ? TILES.CIABATTA2
            : TILES.CIABATTA1;
        this.animationFramesLeft = 30;
      }
    }
  }

  takeDamage(fromBody) {
    sounds.playSfx(SFX.THUMP);

    this.level.removeLivePlacement(fromBody);
    this.healthPoints -= 1;

    // Flash in pain
    this.painFramesRemaining = 20;

    // If dead, start counting to disappear
    if (this.isDead) {
      this.deathDisappearFramesRemaining = 120;
      sounds.stopMusic();
    }

    //this.level.emit();
  }

  damagesBodyOnCollide(forBody) {
    return forBody.type === PLACEMENT_TYPE_HERO;
  }

  handleEnemyCollideWithHero() {
    this.level.setDeathType(this.type);
  }

  renderComponent() {
    return (
      <CharacterBody
        bodyFrameCoord={this.tileFrame}
        hasShadow={false}
        bodySpriteSize={48}
        bodyOffsetLeft={-16}
        bodyOffsetTop={-33}
      />
    );
  }
}
